import {useFeatureIsOn} from "@growthbook/growthbook-react";
import {t} from "@lingui/core/macro";
import {useCallback, useEffect} from "react";

import type {AppFeatures} from "@khan/analytics-growthbook/app-features";
import {publishAnalyticsEventForMinorConversion} from "@khan/analytics-learning-time";
import {
    updateConversationWithSystemMessage,
    useGuideAudioController,
} from "@khan/guide-core";
import {GuideStateApi, useGuideState} from "@khan/guide-state";
import {strings} from "@khan/guide-utils";
import {useLocation} from "@khan/urls";

import {useTextToSpeechOnboarding} from "../text-to-speech/use-text-to-speech-onboarding.ts";
import {isTextToSpeechDisabledForPath} from "../text-to-speech/utils/is-text-to-speech-disabled-for-path.ts";

import {HeadphoneButton} from "./headphone-button.tsx";

export const ConnectedHeadphoneButton = ({disabled}: {disabled: boolean}) => {
    const {
        isPlaying,
        isTextToSpeechSupported,
        isVoiceOn,
        setIsVoiceOn,
        selectedVoice,
    } = useGuideAudioController();

    const navigation = useGuideState("navigation");
    const threadId = useGuideState("threadId");
    const {initiateOnboarding} = useTextToSpeechOnboarding();

    // We disable text-to-speech for the historical figure activity.
    const {pathname} = useLocation();
    const isHeadphoneButtonDisabledForActivity =
        isTextToSpeechDisabledForPath(pathname);

    const isTextToSpeechUnavailable = useFeatureIsOn<AppFeatures>(
        "gl-text-to-speech-unavailable",
    );

    useEffect(() => {
        if (isTextToSpeechUnavailable) {
            setIsVoiceOn(false);
        }
    }, [isTextToSpeechUnavailable, setIsVoiceOn]);

    const isHeadphoneButtonDisabled =
        disabled ||
        isTextToSpeechUnavailable ||
        isHeadphoneButtonDisabledForActivity;

    const handleToggleTextToSpeech = useCallback(() => {
        setIsVoiceOn((currValue) => {
            // We force the user to go through the onboarding flow before
            // allowing them to use the text-to-speech feature if they
            // have not yet selected a voice.
            if (!selectedVoice) {
                initiateOnboarding();
                return currValue;
            }
            const nextValue = !currValue;
            GuideStateApi.set(
                "conversation",
                updateConversationWithSystemMessage(
                    GuideStateApi.latest().conversation,
                    nextValue
                        ? t`Khanmigo voice enabled`
                        : t`Khanmigo voice disabled`,
                ),
            );
            publishAnalyticsEventForMinorConversion({
                eventType: "AiGuideTextToSpeechToggled",
                eventSchemaVersion: 1,
                aiGuideThreadId: threadId ?? "",
                aiGuideNavigationId: navigation.id,
                isTextToSpeechOn: nextValue,
            });
            return nextValue;
        });
    }, [initiateOnboarding, navigation, selectedVoice, setIsVoiceOn, threadId]);

    if (!isTextToSpeechSupported) {
        return null;
    }

    return (
        <HeadphoneButton
            disabled={isHeadphoneButtonDisabled}
            on={isVoiceOn}
            speaking={isPlaying}
            tooltip={
                isHeadphoneButtonDisabledForActivity
                    ? t`Voice is not available for this activity.`
                    : strings.headphoneIconTooltip(
                          isVoiceOn,
                          isHeadphoneButtonDisabled,
                          isTextToSpeechUnavailable,
                      )
            }
            onClick={handleToggleTextToSpeech}
        />
    );
};
